import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, XIcon } from 'lucide-react';
import { WritingPost } from '../lib/cmsTypes';
interface WritingModalProps {
  post: WritingPost;
  onClose: () => void;
}
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.15 + i * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function WritingModal({
  post,
  onClose
}: WritingModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 bg-background overflow-y-auto" data-id="element-492">
      {/* Close button */}
      <button onClick={onClose} className="fixed top-6 right-6 md:top-8 md:right-12 z-50 p-2 text-muted-foreground hover:text-foreground transition-colors duration-200" aria-label="Close" data-id="element-493">
        <XIcon className="w-6 h-6" data-id="element-494" />
      </button>

      <div className="min-h-screen flex flex-col" data-id="element-495">
        {/* Main content area */}
        <div className="flex-1 w-full max-w-[720px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-id="element-496">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center gap-2 mb-8 md:mb-10" data-id="element-497">
            <span className="font-mono text-xs text-primary tracking-[0.2em] uppercase" data-id="element-498">
              // Writing
            </span>
            {post.categories?.length > 0 && <>
                <span className="font-mono text-xs text-muted-foreground" data-id="element-499">
                  {'>'}
                </span>
                <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase" data-id="element-500">
                  {post.categories[0]}
                </span>
              </>}
          </motion.div>

          {/* Title */}
          <motion.h1 custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="font-display text-[clamp(2.5rem,6vw,5rem)] font-bold leading-[1.1] tracking-tight mb-6 text-foreground" data-id="element-501">
            {post.title}
          </motion.h1>

          {post.subtitle && <motion.p custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="font-mono text-lg text-muted-foreground mb-8" data-id="element-502">
              {post.subtitle}
            </motion.p>}

          {/* Tags & Meta */}
          <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="flex flex-wrap items-center gap-4 mb-16 md:mb-20 pb-8 border-b border-border" data-id="element-503">
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-504">
              {new Date(post.publishDate).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </span>

            {post.readingTime && <>
                <span className="w-1 h-1 rounded-full bg-border" data-id="element-505" />
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-506">
                  {post.readingTime}
                </span>
              </>}

            {post.tags?.length > 0 && <>
                <span className="w-1 h-1 rounded-full bg-border" data-id="element-507" />
                <div className="flex gap-2" data-id="element-508">
                  {post.tags.map(tag => <span key={tag} className="font-mono text-xs text-primary uppercase tracking-widest" data-id="element-509">
                      #{tag}
                    </span>)}
                </div>
              </>}
          </motion.div>

          {/* Featured Image */}
          {post.featuredImage && <motion.div custom={4} variants={sectionVariants} initial="hidden" animate="visible" className="mb-16" data-id="element-510">
              <img src={post.featuredImage} alt={post.title} className="w-full aspect-video object-cover border border-border" data-id="element-511" />
            </motion.div>}

          {/* Content */}
          {post.content && <motion.div custom={5} variants={sectionVariants} initial="hidden" animate="visible" className="prose prose-invert prose-p:font-mono prose-p:text-sm prose-p:leading-relaxed prose-p:text-muted-foreground prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight prose-a:text-primary hover:prose-a:text-primary/80 max-w-none" dangerouslySetInnerHTML={{
          __html: post.content
        }} data-id="element-512" />}

          <div className="border-t border-border mt-20" data-id="element-513" />

          {/* Footer navigation */}
          <motion.div custom={6} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center justify-between py-8 md:py-10" data-id="element-514">
            <button onClick={onClose} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" aria-label="Back to writing" data-id="element-515">
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" data-id="element-516" />
              Back to Thoughts
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}